import { setSessionStorageWithExpiry } from "@redotech/redo-web/utils/session-storage-wrapper";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartChange,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import { CurrencyCode } from "@redotech/shopify-client/storefront.graphql";
import { REDO_CART_STORAGE_KEY } from "../utils/cart";
import { BigcommerceGraphQLClient } from "./bigcommerce-graphql";

/**
 * @see https://developer.bigcommerce.com/docs/graphql-storefront
 * @see https://developer.bigcommerce.com/graphql-storefront/reference
 */
interface Checkout {
  entityId: string;
  subtotal: { value: number; currencyCode: string };
  grandTotal: { value: number; currencyCode: string };
  taxTotal: { value: number; currencyCode: string };
  shippingCostTotal: { value: number; currencyCode: string };
}

export class BigcommerceStorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.BIGCOMMERCE;
  graphQLClient: BigcommerceGraphQLClient;
  auth: string;
  storeUrl: string;
  cartEntityId?: string;

  constructor({
    auth,
    storeUrl,
    cartEntityId,
  }: {
    auth: string;
    storeUrl: string;
    cartEntityId?: string;
  }) {
    this.auth = auth;
    this.storeUrl = storeUrl;
    this.cartEntityId = cartEntityId;
    this.graphQLClient = new BigcommerceGraphQLClient(auth);
  }

  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params: URLSearchParams,
    preventReload?: boolean,
  ): Promise<void> {
    if (data.items.length !== 1) {
      return;
    }

    const query = `
      mutation addCartLineItems($addCartLineItemsInput: AddCartLineItemsInput!) {
        cart {
          addCartLineItems(input: $addCartLineItemsInput) {
            cart {
              entityId
            }
          }
        }
      }
    `;

    await this.graphQLClient.query({
      query,
      variables: {
        addCartLineItemsInput: {
          cartEntityId: this.cartEntityId,
          data: {
            lineItems: data.items.map((item) => ({
              productEntityId: item.product_id,
              variantEntityId: item.variant_id || item.id, // FIXME: Canonically, a ShopifyCartItem.id refers to the line item ID not a variant ID and they are not convertible. The rest of manager uses id as a variant id though, so leaving for now.
              quantity: item.quantity,
            })),
          },
        },
      },
    });
  }

  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    // throw new Error("Bigcommerce Unsupported");

    const query = `
      query getCart {
        site {
          cart {
            entityId
            currencyCode
            metafields(first: 50, namespace: "bc_storefront") {
              edges {
                node {
                  entityId
                  key
                  value
                }
              }
            }
            lineItems {
              physicalItems {
                entityId
                parentEntityId
                variantEntityId
                productEntityId
                name
                brand
                sku
                url
                quantity
                listPrice {
                  currencyCode
                  value
                }
                originalPrice {
                  currencyCode
                  value
                }
                salePrice {
                  currencyCode
                  value
                }
                extendedListPrice {
                  currencyCode
                  value
                }
                extendedSalePrice {
                  currencyCode
                  value
                }
                isShippingRequired
              }
              digitalItems {
                entityId
                parentEntityId
                variantEntityId
                productEntityId
                name
                brand
                sku
                url
                quantity
                listPrice {
                  currencyCode
                  value
                }
                originalPrice {
                  currencyCode
                  value
                }
                salePrice {
                  currencyCode
                  value
                }
                extendedListPrice {
                  currencyCode
                  value
                }
                extendedSalePrice {
                  currencyCode
                  value
                }
              }
              giftCertificates {
                entityId
                name
                amount {
                  currencyCode
                  value
                }
                isTaxable
              }
            }
            amount {
              currencyCode
              value
            }
          }
          checkout {
            grandTotal {
              currencyCode
              value
            }
          }
        }
      }
    `;

    const json = await this.graphQLClient.query({ query });
    const cartData = json.data?.site.cart;

    if (cartData?.entityId) {
      this.cartEntityId = cartData.entityId;
    }

    const cartItems: any[] = []; // TODO: type

    if (cartData?.lineItems?.physicalItems) {
      for (const item of cartData.lineItems.physicalItems) {
        cartItems.push({
          id: item.variantEntityId, // Technically not accurate implementation because should be line item ID but matches remaining manager implementation
          line_id: item.entityId,
          quantity: item.quantity,
          product_id: item.productEntityId,
          variant_id: item.variantEntityId,
          handle: item.productEntityId,
          title: item.name,
          vendor: item.brand,
          requires_shipping: item.isShippingRequired || true,
          line_price: item.salePrice.value * item.quantity * 100,
        });
      }
    }
    if (cartData?.lineItems?.digitalItems) {
      for (const item of cartData.lineItems.digitalItems) {
        cartItems.push({
          id: item.variantEntityId,
          line_id: item.entityId,
          quantity: item.quantity,
          product_id: item.productEntityId,
          variant_id: item.variantEntityId,
          handle: item.productEntityId,
          title: item.name,
          vendor: item.brand,
          requires_shipping: false,
          line_price: item.salePrice.value * item.quantity * 100,
        });
      }
    }
    if (cartData?.lineItems?.giftCertificates) {
      for (const item of cartData.lineItems.giftCertificates) {
        cartItems.push({
          id: item.entityId,
          line_id: item.entityId,
          product_id: `gift_card_${item.entityId}`,
          variant_id: `gift_card_${item.entityId}`,
          title: item.name,
          vendor: item.name, // This field doesn't exist in Bigcommerce
          requires_shipping: false,
          line_price: item.amount.value * 100,
        });
      }
    }

    const attributes: Record<string, string> = {};
    if (cartData?.metafields?.edges) {
      for (const edge of cartData.metafields.edges) {
        attributes[edge.node.key] = edge.node.value;
      }
    }

    const returnVal: ShopifyCart = {
      attributes,
      items: cartItems,
      currency: cartData?.currencyCode ?? CurrencyCode.Usd,
      total_price:
        json.data?.site?.checkout?.grandTotal?.value ?? cartData?.amount?.value,
    };

    return returnVal;
  }

  async cartChange(
    change: ShopifyCartChange,
    signal?: AbortSignal,
    preventRefresh: boolean = false,
  ): Promise<void> {
    if (change.quantity) {
      return;
    }

    if (!this.cartEntityId) {
      await this.cartGet();
    }

    const query = `
      mutation CartLineRemove($line: DeleteCartLineItemInput!) {
        cart {
          deleteCartLineItem(input: $line) {
            cart {
              entityId
            }
            deletedLineItemEntityId
            deletedCartEntityId
          }
        }
      }
    `;

    await this.graphQLClient.query({
      query,
      variables: {
        line: {
          cartEntityId: this.cartEntityId,
          lineItemEntityId: change.line,
        },
      },
    });

    if (!preventRefresh) {
      // Since we are going to reload faster than we can update the local storage cart
      // we need to clear the local storage cart so that the cart is re-fetched
      setSessionStorageWithExpiry(REDO_CART_STORAGE_KEY, undefined, 0.15);
      location.reload();
    }
  }

  async updateCartAttribute({
    attributes,
  }: {
    attributes: { key: string; value: string }[];
  }): Promise<ShopifyCart> {
    // First get existing metafields
    const getQuery = `
      query getCartMetafields {
        site {
          cart {
            metafields(first: 50, namespace: "bc_storefront") {
              edges {
                node {
                  entityId
                  key
                  value
                }
              }
            }
          }
        }
      }
    `;

    const response = await this.graphQLClient.query({ query: getQuery });
    const existingMetafields = response.data.site.cart.metafields.edges.map(
      (edge: any) => edge.node,
    );

    // Prepare mutations based on comparison
    const mutations = attributes
      .map((attr, index) => {
        const existingMetafield = existingMetafields.find(
          (m: any) => m.key === attr.key,
        );

        if (!attr.value) {
          // Delete if exists and new value is empty
          return existingMetafield
            ? `
          deleteMetafield${index}: deleteCartMetafield(input: {
            cartEntityId: "${this.cartEntityId}",
            metafieldEntityId: ${existingMetafield.entityId}
          }) {
            deletedMetafieldEntityId
          }
        `
            : null;
        }

        if (existingMetafield) {
          // Update existing
          return `
          updateMetafield${index}: updateCartMetafield(input: {
            cartEntityId: "${this.cartEntityId}",
            metafieldEntityId: ${existingMetafield.entityId},
            data: {
              key: "${attr.key}",
              value: "${attr.value}"
            }
          }) {
            metafield {
              entityId
              key
              value
            }
          }
        `;
        }

        // Create new
        return `
        createMetafield${index}: createCartMetafield(input: {
          cartEntityId: "${this.cartEntityId}",
          data: {
            key: "${attr.key}",
            value: "${attr.value}"
          }
        }) {
          metafield {
            entityId
            key
            value
          }
        }
      `;
      })
      .filter(Boolean); // Remove null mutations

    if (mutations.length === 0) {
      return this.cartGet();
    }

    const mutation = `
      mutation updateCartMetafields {
        cart {
          ${mutations.join("\n")}
        }
      }
    `;

    await this.graphQLClient.query({ query: mutation });
    return this.cartGet();
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
    preventRefresh: boolean = false,
  ): Promise<undefined> {
    const newQuantity = item.quantity - numToRemove;

    await this.cartChange(
      {
        id: String(item.variant_id || item.id),
        quantity: newQuantity,
        line: item.line_id,
      },
      signal,
      preventRefresh,
    );
  }

  async updateCartItemSellingPlan(): Promise<undefined> {}

  async cartClear(): Promise<void> {}

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    if (!handle) {
      console.warn("Bigcommerce product handle is missing.");
      return undefined;
    }

    const query = `
        query getProductById($productId: Int!) {
          site {
            product(entityId: $productId) {
              id
              entityId
              type
              categories {
                edges {
                  node {
                    entityId
                    name
                  }
                }
              }
              variants {
                edges {
                  node {
                    entityId
                    prices {
                      price {
                        value
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

    const json = await this.graphQLClient.query({
      query,
      variables: { productId: isNaN(Number(handle)) ? handle : Number(handle) },
    });

    return json.data.site.product.categories.edges.map((categoryEdge: any) => ({
      title: categoryEdge.node.name,
      handle: String(categoryEdge.node.entityId),
    }));
  }

  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    if (!this.cartEntityId) {
      await this.cartGet();
    }

    const query = `
      query getProductById($productId: Int!) {
        site {
          product(entityId: $productId) {
            id
            entityId
            type
            variants {
              edges {
                node {
                  entityId
                  prices {
                    price {
                      value
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    // TODO: may need to handle currency, unclear what's presentment vs transaction vs shop - ignoring for MVP, assume its transaction

    const json = await this.graphQLClient.query({
      query,
      variables: { productId: isNaN(Number(handle)) ? handle : Number(handle) },
    });

    return {
      id: json.data.site.product.entityId,
      tags: "",
      title: json.data.site.product.name,
      variants: json.data.site.product.variants.edges.map(
        (variantEdge: any) => ({
          id: variantEdge.node.entityId,
          price: variantEdge.node.prices.price.value * 100,
          requires_shipping: json.data.site.product.type === "Physical",
        }),
      ),
    };
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    const query = `
      query getProductById($productId: Int!) {
        site {
          product(entityId: $productId) {
            id
            entityId
            type
            prices {
              priceRange {
                min {
                  value
                  currencyCode
                }
                max {
                  value
                  currencyCode
                }
              }
              price {
                value
                currencyCode
              }
            }
            variants(first: 100) {
              edges {
                node {
                  entityId
                  prices {
                    price {
                      value
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    // TODO: may need to handle currency, unclear what's presentment vs transaction vs shop - ignoring for MVP, assume its transaction

    const json = await this.graphQLClient.query({
      query,
      variables: { productId: isNaN(Number(handle)) ? handle : Number(handle) },
    });

    return {
      id: json.data.site.product.entityId,
      price_min: json.data.site.product.prices.priceRange.min.value * 100,
      price_max: json.data.site.product.prices.priceRange.max.value * 100,
      variants: json.data.site.product.variants.edges.map(
        (variantEdge: any) => ({
          id: variantEdge.node.entityId,
          price: variantEdge.node.prices.price.value * 100,
          requires_shipping: json.data.site.product.type === "Physical",
        }),
      ),
    };
  }

  async productRecommendations(): Promise<never> {
    throw new Error("Unsupported");
  }

  async productsGet(): Promise<never> {
    throw new Error("Unsupported");
  }

  async getStorefrontAccessTokens(accessToken: string): Promise<never> {
    throw new Error("Unsupported");
  }

  async deleteStorefrontAccesstoken(): Promise<never> {
    throw new Error("Unsupported");
  }

  async createStorefrontAccessToken(): Promise<never> {
    throw new Error("Unsupported");
  }

  async productsSearch(): Promise<never> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(data: {
    key: string;
    namespace: string;
  }): Promise<ShopifyShop | undefined> {
    throw new Error("Bigcommerce Unsupported");
  }

  async deleteCartAttribute(data: { key: string }): Promise<void> {
    throw new Error("Bigcommerce Unsupported");
  }

  async getCheckout(): Promise<Checkout | null> {
    const query = `
      query getCheckout {
        site {
          checkout {
            entityId
            subtotal {
              value
              currencyCode
            }
            grandTotal {
              value
              currencyCode
            }
            taxTotal {
              value
              currencyCode
            }
            shippingCostTotal {
              value
              currencyCode
            }
          }
        }
      }
    `;

    const response = await this.graphQLClient.query({ query });
    return response.data.site.checkout;
  }
}
